import { Link } from 'react-router-dom';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { BookOpen, Send } from 'lucide-react';
import { api } from '../../lib/api';

const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
);
const InstagramIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
);
const TwitterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
);
const YoutubeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
);

const columns = [
  {
    title: 'Explore',
    links: [
      { label: 'Shop All Books', to: '/shop' },
      { label: 'Authors', to: '/authors' },
      { label: 'Blog', to: '/blog' },
      { label: 'Membership', to: '/membership' },
    ],
  },
  {
    title: 'For Authors',
    links: [
      { label: 'Publish With Us', to: '/publish' },
      { label: 'Author Dashboard', to: '/publish/dashboard' },
      { label: 'Royalty Calculator', to: '/publish#royalty' },
      { label: 'Affiliate Program', to: '/affiliate' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'Help Center', to: '/support' },
      { label: 'Contact Us', to: '/contact' },
      { label: 'Refund & Cancellation Policy', to: '/legal/refund-policy' },
      { label: 'Shipping & Delivery Policy', to: '/legal/shipping-policy' },
    ],
  },
  {
    title: 'Company',
    links: [
      { label: 'About Us', to: '/about' },
      { label: 'Privacy Policy', to: '/legal/privacy' },
      { label: 'Terms of Service', to: '/legal/terms' },
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!email.trim()) {
      setEmailError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setEmailError('Enter a valid email');
      return;
    }
    setEmailError('');

    setSubmitting(true);
    try {
      const data = await api.post('/newsletter/subscribe', { email: email.trim() });
      toast.success(data.message || 'Subscribed! Check your inbox for a welcome note.');
      setEmail('');
    } catch (err) {
      toast.error(err.message || 'Could not subscribe right now. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="mt-24 border-t border-slate-100 dark:border-white/10 bg-white dark:bg-surface-dark">
      {/* Newsletter + app banner */}
      <div className="container-app py-14">
        <div className="relative overflow-hidden rounded-xl3 bg-gradient-primary px-6 py-12 sm:px-14 text-white">
          <div className="absolute inset-0 bg-gradient-radial-glow" />
          <div className="relative grid gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <p className="eyebrow !text-white/80">Stay in the loop</p>
              <h3 className="mt-2 font-display text-2xl sm:text-3xl font-semibold">
                New releases, curated picks, and members-only deals — weekly.
              </h3>
              <form onSubmit={handleSubscribe} className="mt-6 max-w-md">
                <div className="flex gap-2">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z0-9@._-]/g, "");
                      setEmail(value);
                      if (emailError) setEmailError('');
                    }}
                    placeholder="you@example.com"
                    className="w-full rounded-full border border-white/30 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-white/60 outline-none focus:border-white/60"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex shrink-0 items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-semibold text-primary-700 transition-transform hover:scale-105 disabled:opacity-60 disabled:hover:scale-100"
                  >
                    <Send size={15} /> {submitting ? 'Subscribing…' : 'Subscribe'}
                  </button>
                </div>
                {emailError && <p className="mt-2 text-xs text-red-100">{emailError}</p>}
              </form>
            </div>
            <div className="flex flex-wrap gap-3 lg:justify-end">
              <span className="rounded-xl border border-white/25 bg-white/10 px-5 py-3 text-sm font-medium backdrop-blur">📱 Get it on Google Play</span>
              <span className="rounded-xl border border-white/25 bg-white/10 px-5 py-3 text-sm font-medium backdrop-blur">🍎 Download on the App Store</span>
            </div>
          </div>
        </div>
      </div>

      {/* Link columns */}
      <div className="container-app grid grid-cols-2 gap-10 pb-12 sm:grid-cols-3 lg:grid-cols-5">
        <div className="col-span-2 sm:col-span-3 lg:col-span-1">
          <Link to="/" className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-primary text-white">
              <BookOpen size={18} />
            </span>
            <span className="font-display text-xl font-semibold text-slate-900 dark:text-white">Book Cloud</span>
          </Link>
          <p className="mt-3 max-w-xs text-sm text-slate-500 dark:text-slate-400">
            A marketplace for readers and independent authors — thoughtfully curated, beautifully delivered.
          </p>
          <div className="mt-4 flex gap-3">
            {[FacebookIcon, InstagramIcon, TwitterIcon, YoutubeIcon].map((Icon, i) => (
              <a key={i} href="#" aria-label="Social link" className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 dark:bg-white/10 text-slate-500 dark:text-slate-300 hover:bg-primary-100 hover:text-primary-700 transition-colors">
                <Icon />
              </a>
            ))}
          </div>
        </div>

        {columns.map((col) => (
          <div key={col.title}>
            <h4 className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">{col.title}</h4>
            <ul className="flex flex-col gap-2.5">
              {col.links.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="text-sm text-slate-500 dark:text-slate-400 hover:text-primary-600 dark:hover:text-primary-300">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="border-t border-slate-100 dark:border-white/10 py-6">
        <div className="container-app flex flex-col-reverse items-center justify-between gap-3 sm:flex-row">
          <p className="text-xs text-slate-400">© {new Date().getFullYear()} Book Cloud Books, Inc. All rights reserved.</p>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1 text-xs text-slate-400">
            <Link to="/legal/privacy" className="hover:text-primary-600">Privacy</Link>
            <Link to="/legal/terms" className="hover:text-primary-600">Terms</Link>
            <Link to="/legal/cookies" className="hover:text-primary-600">Cookies</Link>
            <Link to="/legal/dmca" className="hover:text-primary-600">Copyright Policy</Link>
            <Link to="/legal/sitemap" className="hover:text-primary-600">Sitemap</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
